import { useState } from "react";
import { usePaxgConsultMutation, usePaxgApplyMutation, type PaxgConsultResponse } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";

/** PAXG → USDT rebalance — Gemini-advised, explicit-consent.
 *
 *  Step 1: click "Ask Gemini". POST /api/paxg/rebalance/consult.
 *          Read-only. Shows verdict (hold | rebalance) + reasons +
 *          risk warnings.
 *  Step 2 (only if verdict=rebalance): click "Apply" to actually sell.
 *          POST /api/paxg/rebalance/apply with the recommended percent.
 *          Backend has a hard 25% cap regardless.
 *
 *  PAXG is the hedge — this card is intentionally not auto-fired.
 */
export function PaxgRebalanceCard() {
  const [consult, { isLoading: consulting, data: result, reset: resetConsult }] =
    usePaxgConsultMutation();
  const [apply, { isLoading: applying }] = usePaxgApplyMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const data = result as PaxgConsultResponse | undefined;
  const verdict = data?.verdict;
  const snap = data?.snapshot;

  async function onConsult() {
    try {
      await consult().unwrap();
      toast.success("Gemini consulted");
    } catch (e: any) {
      toast.error(`Consult failed: ${e?.data?.detail ?? "?"}`);
    }
  }
  async function onApply() {
    if (!verdict || verdict.action !== "rebalance") return;
    try {
      const r = await apply({ percent: verdict.percent_to_move }).unwrap();
      toast.success(`Rebalance done: $${r.usdt_received ?? "?"} USDT`);
      setConfirmOpen(false);
      resetConsult();
    } catch (e: any) {
      toast.error(`Apply failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-baseline gap-2 flex-wrap">
          <span>PAXG Rebalance</span>
          <span className="text-xs text-muted font-normal">
            convert hedge → USDT war chest for aggressive DCA
          </span>
        </CardTitle>
        <Button size="sm" onClick={onConsult} disabled={consulting}>
          {consulting ? "Asking Gemini…" : "Ask Gemini"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {data && data.status === "error" && (
          <Alert tone="danger">Error: {data.reason ?? "unknown"}</Alert>
        )}

        {snap && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-muted">PAXG holding</div>
              <div className="font-semibold">
                {snap.paxg_holding_qty.toFixed(6)} oz
              </div>
              <div className="text-muted">≈ {fmtUsd(snap.paxg_holding_usd)}</div>
            </div>
            <div>
              <div className="text-muted">USDT free</div>
              <div className="font-semibold">{fmtUsd(snap.usdt_holding_usd)}</div>
              {snap.usdt_pct_of_crypto != null && (
                <div className="text-muted">
                  {snap.usdt_pct_of_crypto.toFixed(1)}% vs crypto
                </div>
              )}
            </div>
            <div>
              <div className="text-muted">Crypto exposure</div>
              <div className="font-semibold">{fmtUsd(snap.crypto_exposure_usd)}</div>
            </div>
            <div>
              <div className="text-muted">BTC RSI daily / 4h</div>
              <div className="font-semibold">
                {snap.btc_rsi_daily?.toFixed(1) ?? "—"} / {snap.btc_rsi_4h?.toFixed(1) ?? "—"}
              </div>
              <div className="text-muted">
                {snap.market_regime}
                {snap.auto_defense_engaged ? " · defense ON" : ""}
              </div>
            </div>
          </div>
        )}

        {verdict && (
          <div className="border rounded p-3 space-y-2">
            <div className="flex items-baseline gap-2 flex-wrap">
              <Badge tone={verdict.action === "rebalance" ? "warn" : "muted"}>
                Gemini verdict: {verdict.action.toUpperCase()}
              </Badge>
              {verdict.action === "rebalance" && (
                <span className="text-sm font-semibold">
                  → move {verdict.percent_to_move}% (≈{" "}
                  {fmtUsd((snap?.paxg_holding_usd ?? 0) * (verdict.percent_to_move / 100))})
                </span>
              )}
              <span className="text-xs text-muted">
                confidence {(verdict.confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="text-sm">{verdict.rationale}</div>
            {verdict.risk_warnings.length > 0 && (
              <div className="text-xs">
                <div className="text-amber-500 font-semibold mb-1">⚠ Risk warnings:</div>
                <ul className="list-disc ml-5 text-muted space-y-0.5">
                  {verdict.risk_warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {verdict.action === "rebalance" && (
              <div className="flex gap-2 pt-2 border-t">
                {!confirmOpen ? (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setConfirmOpen(true)}
                  >
                    Apply rebalance…
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={onApply}
                      disabled={applying}
                    >
                      {applying
                        ? "Selling…"
                        : `Confirm sell ${verdict.percent_to_move}% PAXG`}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmOpen(false)}
                      disabled={applying}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {!data && (
          <div className="text-xs text-muted">
            Click <b>Ask Gemini</b> to get a hold/rebalance verdict with risk warnings.
            Nothing is sold until you explicitly confirm.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
